import { useState } from 'react';
import toast from 'react-hot-toast';
import {
  OVERLAY_OPACITY_MAX,
  OVERLAY_OPACITY_MIN,
  OVERLAY_POSITIONS,
  OVERLAY_SIZE_PCT_MAX,
  OVERLAY_SIZE_PCT_MIN,
  type OverlayPosition,
} from '@seencast/shared';
import { Modal } from '../../components/Modal.tsx';
import { MediaPickerModal } from '../../components/MediaPickerModal.tsx';
import { MediaThumb } from '../../components/MediaThumb.tsx';
import { Button, Field, TextField, SelectField } from '../../components/ui.tsx';
import { OverlayPreview } from './OverlayPreview.tsx';
import { overlayPreviewModel, clampSize, clampOpacity } from '../../lib/overlay-preview.ts';
import { useCreateOverlay, useUpdateOverlay, type Overlay } from '../../api/overlays.ts';
import { useMedia } from '../../api/media.ts';
import { ApiError } from '../../api/client.ts';

const POSITION_LABEL: Record<OverlayPosition, string> = {
  br: 'Bottom-right',
  bl: 'Bottom-left',
  tr: 'Top-right',
  tl: 'Top-left',
};

/** Create or edit an overlay, with a live preview. `overlay` present → edit mode. */
export function OverlayForm({
  overlay,
  accent,
  onClose,
}: {
  overlay?: Overlay;
  accent?: string | null;
  onClose: () => void;
}) {
  const create = useCreateOverlay();
  const update = useUpdateOverlay();
  const media = useMedia({ kind: 'image' });

  const [name, setName] = useState(overlay?.name ?? '');
  const [logoId, setLogoId] = useState<string | null>(overlay?.logo_media_id ?? null);
  const [position, setPosition] = useState<OverlayPosition>(overlay?.position ?? 'br');
  const [sizePct, setSizePct] = useState<number>(overlay?.size_pct ?? 10);
  const [opacity, setOpacity] = useState<number>(overlay?.opacity ?? 90);
  const [tickerText, setTickerText] = useState(overlay?.ticker_text ?? '');
  const [picking, setPicking] = useState(false);

  const values = { name, logo_media_id: logoId, position, size_pct: sizePct, opacity, ticker_text: tickerText };
  const model = overlayPreviewModel(values);
  const busy = create.isPending || update.isPending;
  const logoMedia = (media.data ?? []).find((m) => m.id === logoId);

  function submit() {
    if (!model.isValid) return;
    const body = {
      name: name.trim(),
      logo_media_id: logoId,
      position,
      size_pct: clampSize(sizePct),
      opacity: clampOpacity(opacity),
      ticker_text: tickerText.trim() ? tickerText.trim() : null,
    };
    const onError = (e: unknown) => toast.error(e instanceof ApiError ? e.message : 'Save failed');
    if (overlay) {
      update.mutate(
        { id: overlay.id, body },
        { onSuccess: () => { toast.success('Overlay saved'); onClose(); }, onError },
      );
    } else {
      create.mutate(body, {
        onSuccess: () => { toast.success('Overlay created'); onClose(); },
        onError,
      });
    }
  }

  return (
    <Modal
      title={overlay ? 'Edit overlay' : 'New overlay'}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={!model.isValid || busy}>
            {busy ? 'Saving…' : overlay ? 'Save overlay' : 'Create overlay'}
          </Button>
        </>
      }
    >
      <div className="ov-form-grid">
        <div>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Storefront logo + hours ticker"
          />

          <Field label="Logo" hint="Optional. Pick an image from your media library.">
            <div className="ov-logo-row">
              {logoId ? (
                <>
                  <MediaThumb
                    media={
                      logoMedia ?? {
                        id: logoId, org_id: '', kind: 'image', status: 'ready', filename: null,
                        mime: null, size_bytes: null, width: null, height: null, duration_ms: null,
                        src_url: null, created_at: 0,
                      }
                    }
                    className="picker-thumb"
                  />
                  <span className="cell-dim" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {logoMedia?.filename ?? 'Selected image'}
                  </span>
                  <Button size="sm" onClick={() => setPicking(true)}>Change</Button>
                  <Button size="sm" variant="ghost" onClick={() => setLogoId(null)}>Remove</Button>
                </>
              ) : (
                <Button size="sm" onClick={() => setPicking(true)}>Choose logo…</Button>
              )}
            </div>
          </Field>

          <SelectField
            label="Position"
            value={position}
            onChange={(e) => setPosition(e.target.value as OverlayPosition)}
          >
            {OVERLAY_POSITIONS.map((p) => (
              <option key={p} value={p}>{POSITION_LABEL[p]}</option>
            ))}
          </SelectField>

          <Field label={`Logo size — ${clampSize(sizePct)}% of screen width`}>
            <input
              type="range"
              aria-label="Logo size"
              min={OVERLAY_SIZE_PCT_MIN}
              max={OVERLAY_SIZE_PCT_MAX}
              value={sizePct}
              onChange={(e) => setSizePct(Number(e.target.value))}
            />
          </Field>

          <Field label={`Opacity — ${clampOpacity(opacity)}%`}>
            <input
              type="range"
              aria-label="Opacity"
              min={OVERLAY_OPACITY_MIN}
              max={OVERLAY_OPACITY_MAX}
              value={opacity}
              onChange={(e) => setOpacity(Number(e.target.value))}
            />
          </Field>

          <TextField
            label="Ticker text"
            hint="Optional. A scrolling strip along the bottom of every screen."
            value={tickerText}
            onChange={(e) => setTickerText(e.target.value)}
            placeholder="e.g. Open Mon–Sat · Free Wi-Fi · Ask about our loyalty card"
          />

          {model.error && <p className="ov-form-error" role="status">{model.error}</p>}
        </div>

        <div className="ov-form-preview">
          <div className="card-title">Live preview</div>
          <OverlayPreview values={values} accent={accent} />
        </div>
      </div>

      {picking && (
        <MediaPickerModal
          title="Choose a logo"
          onPick={(id) => { setLogoId(id); setPicking(false); }}
          onClose={() => setPicking(false)}
        />
      )}
    </Modal>
  );
}
